import { BaseFile, UploadedFile } from '../../../../../../common/domain/upload';
import { isTooLongStringInput, isValidUrl } from '../../../../../../common/validations';
import { BaseFormProblem, BaseFormSoftWarnings } from '../../SubmitGame/submission-errors';
import isEmptyString from '../../../../../../common/is-empty-string';
import slugify, { isValidSlug } from '../../../../../../common/slug';
import { GameJamId } from '@crazygames/gamejam/game-jam-config';
import { ProgressSaveType } from '@crazygames/game-types/src/game-build/aps.type';
import {
  GameLoaderType,
  LOADERS_WITH_HTML5_FALLBACK,
  UNITY_GAME_LOADERS,
  UNITY_LOADERS_WITH_DATA_SAVE,
} from '@crazygames/game-types/src/game-build/loader.type';
import { FullscreenSetting } from '@crazygames/game-types/src/game-build/fullscreen.type';
import { GameOrientation } from '@crazygames/game-types/src/game-build/orientation.type';
import { CUSTOM_BUILD_EXCLUDE_PATTERNS } from '@crazygames/game-types/src/game-build/custom-build.type';

export const MAX_NO_TAGS = 5;

// developer cannot submit if total size > 250MB or if there are more than 1500 files, however these hard warnings aren't applied when updating
export const MAX_TOTAL_FILE_SIZE_MB = 250;
export const MAX_TOTAL_FILES_COUNT = 1500;

// these are for the soft warnings (yellow warnings), that allow you to submit
export const DESKTOP_INITIAL_LOAD_LIMIT_MB = 50;
export const MOBILE_INITIAL_LOAD_LIMIT_MB = 20;
export const MAX_LOBBY_SIZE = 999;
export const MIN_LOBBY_SIZE = 2;

export interface MultiplayerOptions {
  isMultiplayer?: boolean;
  supportsInstantJoin?: boolean;
  hasModeratedChat?: boolean;
  minLobbySize?: number;
  maxLobbySize?: number;
}

export interface BaseFormData {
  name: string | null;
  gameLoaderType: GameLoaderType | null;
  fullscreen: FullscreenSetting;
  isIOSFriendly: boolean;
  isAndroidFriendly: boolean;
  orientation: GameOrientation | null;
  iframeLink: string | null;
  files: UploadedFile[] | null;
  uploadInProgress: boolean;
  unitySaveFileName?: string;
  progressSaveType?: ProgressSaveType;
  hasIAP: boolean;
  isChromebookFriendly: boolean;
  multiplayerOptions?: MultiplayerOptions;
  gameJamId?: GameJamId;
}

export function buildDefaultBaseFormData(): BaseFormData {
  return {
    name: null,
    gameLoaderType: null,
    fullscreen: 'SUPPORTED',
    isIOSFriendly: false,
    isAndroidFriendly: false,
    orientation: null,
    iframeLink: null,
    files: null,
    uploadInProgress: false,
    progressSaveType: 'UNKNOWN',
    hasIAP: false,
    isChromebookFriendly: false,
    multiplayerOptions: undefined,
    gameJamId: undefined,
  };
}

export function validateBaseForm(form: BaseFormData, skipFileChecks: boolean): BaseFormProblem[] {
  const problems: BaseFormProblem[] = [
    ...validateName(form),
    ...validateGameLoaderType(form),
    ...validateOrientation(form),
    ...validateMultiplayerLobbySize(form.multiplayerOptions),
    ...validateGameJam(form),
  ];

  if (!skipFileChecks) {
    problems.push(...validateFileLimitsHard(form.files || []));
  }

  return problems;
}
function validateName(form: BaseFormData): BaseFormProblem[] {
  const { name } = form;
  if (isEmptyString(name)) {
    return ['NAME_MISSING'];
  }
  const trimmed = (name as string).trimLeft();
  const slug = slugify(trimmed);
  if (!isValidSlug(slug)) {
    return ['NAME_INVALID'];
  }
  return [];
}

export function validateOrientation(form: BaseFormData): BaseFormProblem[] {
  const { isAndroidFriendly, isIOSFriendly, orientation } = form;

  if (!isAndroidFriendly && !isIOSFriendly) {
    return [];
  }

  if (!orientation) {
    return ['ORIENTATION_MISSING'];
  }

  return [];
}

export function computeTotalSize(files: BaseFile[]): number {
  const getBaseName = (path: string): string => {
    const idx = path.lastIndexOf('/');
    return idx >= 0 ? path.slice(idx + 1) : path;
  };

  return files.reduce((acc, file) => {
    const name = getBaseName(file.path);
    return CUSTOM_BUILD_EXCLUDE_PATTERNS.some((r) => r.test(name)) ? acc : acc + file.size;
  }, 0);
}

export function validateFileLimitsSoft(files: BaseFile[]): BaseFormSoftWarnings[] {
  const warnings: BaseFormSoftWarnings[] = [];
  const totalSize = computeTotalSize(files);

  if (totalSize > DESKTOP_INITIAL_LOAD_LIMIT_MB * 1024 * 1024) {
    warnings.push('TOTAL_FILE_SIZE_TOO_BIG');
  }

  return warnings;
}

export function validateFileLimitsHard(files: BaseFile[]): BaseFormProblem[] {
  const problems: BaseFormProblem[] = [];
  const totalSize = computeTotalSize(files);

  if (files.length > MAX_TOTAL_FILES_COUNT) {
    problems.push('TOTAL_FILE_COUNT_EXCEEDED');
  }

  if (totalSize > MAX_TOTAL_FILE_SIZE_MB * 1024 * 1024) {
    problems.push('TOTAL_FILE_SIZE_EXCEEDED');
  }

  return problems;
}

function validateMultiplayerLobbySize(multiplayerOptions: MultiplayerOptions | undefined): BaseFormProblem[] {
  const problems: BaseFormProblem[] = [];

  // if at least one of the lobby size options is set, both must be correctly set
  if (
    (multiplayerOptions?.minLobbySize !== undefined && multiplayerOptions?.minLobbySize !== null) ||
    (multiplayerOptions?.maxLobbySize !== undefined && multiplayerOptions?.maxLobbySize !== null)
  ) {
    if (
      typeof multiplayerOptions?.minLobbySize !== 'number' ||
      typeof multiplayerOptions?.maxLobbySize !== 'number' ||
      multiplayerOptions?.maxLobbySize < multiplayerOptions?.minLobbySize
    ) {
      problems.push('MULTIPLAYER_INVALID_LOBBY');
    }

    if (typeof multiplayerOptions?.minLobbySize === 'number' && multiplayerOptions?.minLobbySize < MIN_LOBBY_SIZE) {
      problems.push('MULTIPLAYER_INVALID_LOBBY_MIN');
    }

    if (typeof multiplayerOptions?.maxLobbySize === 'number' && multiplayerOptions.maxLobbySize > MAX_LOBBY_SIZE) {
      problems.push('MULTIPLAYER_INVALID_LOBBY_MAX');
    }
  }
  return problems;
}

function validateGameLoaderType(form: BaseFormData): BaseFormProblem[] {
  const { gameLoaderType, uploadInProgress } = form;

  if (gameLoaderType === null) {
    return ['GAME_LOADER_TYPE_MISSING'];
  }

  if (gameLoaderType === 'iframe') {
    return validateIframeForm(form);
  }

  if (uploadInProgress) {
    return ['GAME_FILE_UPLOAD_IN_PROGRESS'];
  }

  if (gameLoaderType === 'html5' || LOADERS_WITH_HTML5_FALLBACK.includes(gameLoaderType)) {
    return validateHTML5Form(form);
  }

  if (UNITY_GAME_LOADERS.includes(gameLoaderType)) {
    return validateUnityForm(form);
  }

  throw new Error(`Unmapped game loader type: ${gameLoaderType}`);
}

function validateHTML5Form(form: BaseFormData): BaseFormProblem[] {
  const { files } = form;
  const problems: BaseFormProblem[] = [];
  if (!files || files.length < 1 || !hasFilePattern(files, /\/?index.html$/)) {
    problems.push('HTML5_FILES_MISSING');
  }
  if (!form.progressSaveType || form.progressSaveType === 'UNKNOWN') {
    problems.push('APS_INFO_MISSING');
  }
  return problems;
}

function validateIframeForm(form: BaseFormData): BaseFormProblem[] {
  const { iframeLink } = form;
  const problems: BaseFormProblem[] = [];
  if (iframeLink === null || isTooLongStringInput(iframeLink) || !isValidUrl(iframeLink)) {
    problems.push('IFRAME_INVALID_URL');
  }
  if (!form.progressSaveType || form.progressSaveType === 'UNKNOWN') {
    problems.push('APS_INFO_MISSING');
  }
  return problems;
}

function validateUnityForm(form: BaseFormData): BaseFormProblem[] {
  const { files } = form;
  const problems: BaseFormProblem[] = [];
  if (!files || files.length < 1) {
    problems.push('UNITY_FILES_MISSING');
  }
  if (form.gameLoaderType && UNITY_LOADERS_WITH_DATA_SAVE.includes(form.gameLoaderType)) {
    if (!form.progressSaveType || form.progressSaveType === 'UNKNOWN') {
      problems.push('APS_INFO_MISSING');
    }
    if (form.progressSaveType === 'CUSTOM') {
      if (!form.unitySaveFileName || form.unitySaveFileName?.length === 0) {
        problems.push('APS_UNITY_CUSTOM_NAME_MISSING');
      } else if (isTooLongStringInput(form.unitySaveFileName)) {
        problems.push('APS_UNITY_CUSTOM_NAME_INVALID');
      }
    }
  }
  return problems;
}

function validateGameJam(form: BaseFormData): BaseFormProblem[] {
  const { gameJamId } = form;
  // We currently have no specific requirements for game jam submissions.
  if (gameJamId) {
    return [];
  }
  return [];
}

function hasFilePattern(files: UploadedFile[], rx: RegExp) {
  return !!files.find(({ path }) => rx.test(path));
}
